// Pure game rules: every function takes a game state and returns values or a
// new state, so the UI and the pacing simulation share exactly the same logic.
import {
    workers, workerUpgradeTiers, workerUpgradeNames, synergies, promotions,
    RELEASE_DIVISOR, REPUTATION_BONUS, CROWD_BONUS_PER_DOUBLING, CROWD_BONUS_CAP,
} from './data.js';

// Bump when the save shape changes in a way migrateSave needs to handle.
export const SAVE_VERSION = 3;

// Each worker owned makes the next one 15% more expensive.
const PRICE_GROWTH = 1.15;

const zeroes = (types) => Object.fromEntries(types.map(type => [type, 0]));

export function defaultGameValues(reputation = { points: 0, releases: 0, earnedBefore: 0 }) {
    const workerTypes = workers.map(w => w.type);
    return {
        version: SAVE_VERSION,
        // lines: spendable; click: lines earned by clicking; earned: all lines earned this run.
        counters: { lines: 0, click: 0, earned: 0, ...zeroes(workerTypes) },
        // upgrades.click is your promotion level; the rest are worker upgrade tiers.
        upgrades: zeroes(['click', ...workerTypes]),
        synergies: [],
        reputation,
    };
}

// Merge a saved game over the defaults, so older saves pick up anything added
// since and unknown fields are dropped.
export function migrateSave(saved) {
    const defaults = defaultGameValues();
    const counters = { ...defaults.counters, ...saved.counters };
    if (!saved.counters || saved.counters.earned === undefined) {
        // Saves before v3 didn't track earnings; count what's in the bank.
        counters.earned = counters.lines;
    }
    const upgrades = { ...defaults.upgrades, ...saved.upgrades };
    const maxTier = workerUpgradeTiers.length;
    for (const worker of workers) {
        upgrades[worker.type] = Math.min(upgrades[worker.type], maxTier);
    }
    return {
        ...defaults,
        counters,
        upgrades,
        synergies: Array.isArray(saved.synergies) ? saved.synergies.filter(id => synergies.some(s => s.id === id)) : [],
        reputation: { ...defaults.reputation, ...saved.reputation },
    };
}

export function getWorkerPrice(worker, owned) {
    return Math.ceil(worker.price * Math.pow(PRICE_GROWTH, owned));
}

export function getReputationMultiplier(state) {
    return 1 + state.reputation.points * REPUTATION_BONUS;
}

// Bonus from other players online right now. `state.crowd` is the live player
// count (including you); it isn't saved, so loaded games and offline
// earnings start solo.
export function getCrowdBonus(state) {
    const players = Math.max(1, state.crowd || 1);
    return Math.min(CROWD_BONUS_CAP, CROWD_BONUS_PER_DOUBLING * Math.log2(players));
}

// Multiplier on everything: reputation from releases and the crowd bonus.
export function getGlobalMultiplier(state) {
    return getReputationMultiplier(state) * (1 + getCrowdBonus(state));
}

export function getPromotion(state) {
    return promotions.find(p => p.level === state.upgrades.click) || null;
}

// Bonus from bought synergies for one worker type, e.g. 0.75 = +75%.
export function getSynergyBonus(state, type) {
    return synergies
        .filter(s => state.synergies.includes(s.id))
        .reduce((bonus, s) => {
            if (s.target === type) bonus += s.bonus * state.counters[s.source];
            if (s.source === type) bonus += s.reverseBonus * state.counters[s.target];
            return bonus;
        }, 0);
}

// Output multiplier for one worker type, from upgrade tiers, synergies, reputation and the crowd.
export function getWorkerMultiplier(state, type) {
    return Math.pow(2, state.upgrades[type]) * (1 + getSynergyBonus(state, type)) * getGlobalMultiplier(state);
}

export function getWorkerCps(state, worker) {
    return worker.cps * getWorkerMultiplier(state, worker.type);
}

// Lines per second from all workers.
export function getCps(state) {
    return workers.reduce((total, worker) => total + state.counters[worker.type] * getWorkerCps(state, worker), 0);
}

export function getClickPower(state) {
    const promotion = getPromotion(state);
    const base = promotion ? promotion.clickPower : 1;
    const share = promotion ? promotion.cpsPercent : 0;
    return base * getGlobalMultiplier(state) + getCps(state) * share;
}

export function addLines(state, amount, fromClick = false) {
    if (amount <= 0) return state;
    return {
        ...state,
        counters: {
            ...state.counters,
            lines: state.counters.lines + amount,
            earned: state.counters.earned + amount,
            click: fromClick ? state.counters.click + amount : state.counters.click,
        },
    };
}

export function click(state) {
    return addLines(state, getClickPower(state), true);
}

export function buyWorker(state, type) {
    const worker = workers.find(w => w.type === type);
    const price = getWorkerPrice(worker, state.counters[type]);
    if (state.counters.lines < price) return state;
    return {
        ...state,
        counters: {
            ...state.counters,
            lines: state.counters.lines - price,
            [type]: state.counters[type] + 1,
        },
    };
}

const titleCase = (text) => text[0].toUpperCase() + text.slice(1);

// Every upgrade you could buy right now (requirements met), cheapest first.
// Each entry has what the store needs to show it and an `apply` that returns
// the state after buying it (minus the price, which buyUpgrade handles).
export function getAvailableUpgrades(state) {
    const available = [];
    const { counters, upgrades } = state;

    const nextPromotion = promotions.find(p => p.level === upgrades.click + 1);
    if (nextPromotion && counters.click >= nextPromotion.requirement) {
        const share = nextPromotion.cpsPercent ? ` plus ${Math.round(nextPromotion.cpsPercent * 100)}% of your lines per second` : '';
        available.push({
            id: `promotion:${nextPromotion.level}`,
            kind: 'promotion',
            name: nextPromotion.name,
            subtitle: nextPromotion.short_description,
            description: nextPromotion.description,
            effect: `Promotion: each click writes ${nextPromotion.clickPower} lines${share}`,
            price: nextPromotion.price,
            icons: [nextPromotion.icon],
            apply: (s) => ({ ...s, upgrades: { ...s.upgrades, click: nextPromotion.level } }),
        });
    }

    for (const worker of workers) {
        const tierIndex = upgrades[worker.type];
        const tier = workerUpgradeTiers[tierIndex];
        if (!tier || counters[worker.type] < tier.owned) continue;
        available.push({
            id: `worker:${worker.type}:${tierIndex + 1}`,
            kind: 'worker',
            name: workerUpgradeNames[worker.type][tierIndex],
            subtitle: `${worker.name} upgrade ${tierIndex + 1} of ${workerUpgradeTiers.length}`,
            description: `${worker.name}s are twice as productive.`,
            effect: `${worker.name} output ×2`,
            price: worker.price * tier.priceMultiplier,
            icons: [worker.icon],
            tier: tierIndex + 1,
            apply: (s) => ({ ...s, upgrades: { ...s.upgrades, [worker.type]: tierIndex + 1 } }),
        });
    }

    for (const synergy of synergies) {
        if (state.synergies.includes(synergy.id)) continue;
        if (counters[synergy.target] < synergy.requires || counters[synergy.source] < synergy.requires) continue;
        const target = workers.find(w => w.type === synergy.target);
        const source = workers.find(w => w.type === synergy.source);
        available.push({
            id: `synergy:${synergy.id}`,
            kind: 'synergy',
            name: synergy.name,
            subtitle: `${target.name} + ${source.name} synergy`,
            description: `${titleCase(target.name)}s and ${source.name}s work better together.`,
            effect: `${target.name}s +${synergy.bonus * 100}% per ${source.name}; ${source.name}s +${synergy.reverseBonus * 100}% per ${target.name}`,
            price: synergy.price,
            icons: [target.icon, source.icon],
            apply: (s) => ({ ...s, synergies: [...s.synergies, synergy.id] }),
        });
    }

    return available.sort((a, b) => a.price - b.price);
}

export function buyUpgrade(state, id) {
    const upgrade = getAvailableUpgrades(state).find(u => u.id === id);
    if (!upgrade || state.counters.lines < upgrade.price) return state;
    const bought = upgrade.apply(state);
    return { ...bought, counters: { ...bought.counters, lines: bought.counters.lines - upgrade.price } };
}

// Reputation you'd gain by shipping a release now.
export function getPendingReputation(state) {
    const allTime = state.reputation.earnedBefore + state.counters.earned;
    return Math.max(0, Math.floor(Math.cbrt(allTime / RELEASE_DIVISOR)) - state.reputation.points);
}

// Reset the run, keeping reputation (plus whatever this release earned).
export function shipRelease(state) {
    const pending = getPendingReputation(state);
    if (pending <= 0) return state;
    return defaultGameValues({
        points: state.reputation.points + pending,
        releases: state.reputation.releases + 1,
        earnedBefore: state.reputation.earnedBefore + state.counters.earned,
    });
}
